import React, { useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import {
  DefaultCard,
  colors,
  moneyFormat,
  helpers,
  PdfPreview,
  SubTitle1Text,
} from 'design-system';
import { Grid, Typography, Divider, makeStyles, Box } from '@material-ui/core';
import { Quotation, QuotationSummaryResponse } from 'data-layer-cr/dist/models';

// Components
import { ImagePreview } from '../../../components';

// Redux Related
import { RootState } from '../../../../../store/rootReducer';

// Functions
import { shouldShowTotalAmount } from '../../../../../functions/quotationMethods';

const { formatMoney } = moneyFormat;

const useStyles = makeStyles({
  fieldLabel: {
    color: colors.abbey,
    fontSize: 14,
  },
  registryPreviewContainer: {
    width: '100%',
    height: 300,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    overflow: 'hidden',
    borderRadius: 10,
  },
  logo: { marginRight: 11 },
});

export const FullVehicleDataCard = ({
  summary,
  vinNumber,
  licensePlate,
  type,
  registryFilename,
  registryBase64,
  serialMotor,
  newCarInvoiceFilename,
  newCarInvoiceBase64,
  preventCollapse = false,
  isOptionsDetails = false,
  quotation,
}: Props): JSX.Element => {
  const classes = useStyles();
  const isPdfOldCar = useMemo(
    () => helpers.isPdf(registryBase64),
    [registryBase64],
  );

  const isPdfNewCar = useMemo(
    () => helpers.isPdf(newCarInvoiceBase64),
    [newCarInvoiceBase64],
  );

  const licensePlateDefaultValue =
    type === 'K' ? `CL${licensePlate}` : licensePlate;

  const profileType = quotation.quotationProfile.type;

  return (
    <DefaultCard
      title={
        <Grid item>
          <Box display="flex">
            <img
              src="svg/steering_wheel.svg"
              alt="steering_wheel_icon"
              className={classes.logo}
              data-testid="icon-image"
            />
            <SubTitle1Text>Datos del Auto</SubTitle1Text>
          </Box>
        </Grid>
      }
      collapsible={!preventCollapse}
      startCollapsed
    >
      <Grid container spacing={2}>
        {!isOptionsDetails && (
          <>
            <Grid item xs={12} md={6} container>
              <Grid item xs={12}>
                <Typography variant="caption" className={classes.fieldLabel}>
                  Marca
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2">{summary.carBrand}</Typography>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6} container>
              <Grid item xs={12}>
                <Typography variant="caption" className={classes.fieldLabel}>
                  Modelo
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2">{summary.carModel}</Typography>
              </Grid>
            </Grid>
          </>
        )}

        {isOptionsDetails && (
          <>
            <Grid item xs={12}>
              <Typography variant="h3">
                {summary.carBrand} {summary.carModel}
              </Typography>
            </Grid>
            <Grid item xs={12} container data-testid="isOptionsDetails-grid">
              <Grid item xs={6} container justifyContent="flex-start">
                <Typography variant="caption" className={classes.fieldLabel}>
                  Año
                </Typography>
              </Grid>
              <Grid item xs={6} container justifyContent="flex-end">
                <Typography variant="body2">{summary.carYear}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid
                container
                item
                justifyContent="center"
                data-testid="sum-assured"
                style={{ marginTop: 22 }}
              >
                <Grid container alignItems="center" item xs={8}>
                  <Typography variant="body1">Suma asegurada</Typography>
                </Grid>
                <Grid
                  container
                  alignItems="center"
                  item
                  xs={4}
                  justifyContent="flex-end"
                >
                  <Typography variant="h2">
                    {summary.insuranceValue === 0
                      ? 'Gratis'
                      : formatMoney(
                          summary.insuranceValue,
                          false,
                          quotation.quotationProfile.typeOfMoney,
                        )}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </>
        )}

        {!isOptionsDetails && (
          <Grid item xs={12} md={6} container>
            <Grid item xs={12}>
              <Typography variant="caption" className={classes.fieldLabel}>
                Año
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2">{summary.carYear}</Typography>
            </Grid>
          </Grid>
        )}

        {shouldShowTotalAmount(summary) && !isOptionsDetails && (
          <Grid item xs={12} md={6}>
            <Typography variant="caption" className={classes.fieldLabel}>
              Suma asegurada
            </Typography>
            <Typography variant="body2">
              {formatMoney(
                summary.insuranceValue,
                false,
                quotation.quotationProfile.typeOfMoney,
              )}
            </Typography>
          </Grid>
        )}

        {!isOptionsDetails && (
          <>
            {/* VIN Number */}
            {vinNumber !== '' && (
              <Grid item xs={12} md={6} data-testid="vin-number-container">
                <Typography variant="caption" className={classes.fieldLabel}>
                  VIN o número de chasis
                </Typography>
                <Typography variant="body2">{vinNumber}</Typography>
              </Grid>
            )}

            {/* License Plate */}
            {licensePlate !== '' && (
              <Grid item xs={12} md={6} data-testid="license-plate-container">
                <Typography variant="caption" className={classes.fieldLabel}>
                  Placa
                </Typography>
                <Typography variant="body2">
                  {licensePlateDefaultValue}
                </Typography>
              </Grid>
            )}

            {/* Motor Number */}
            {serialMotor !== '' && (
              <Grid item xs={12} md={6} data-testid="motor-container">
                <Typography variant="caption" className={classes.fieldLabel}>
                  Número de motor
                </Typography>
                <Typography variant="body2">{serialMotor}</Typography>
              </Grid>
            )}

            {/* Registry image */}
            {registryBase64 !== '' && (
              <Grid item xs={12} data-testid="vehicle-registry-container">
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      className={classes.fieldLabel}
                    >
                      Registro vehicular
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    {isPdfOldCar ? (
                      <PdfPreview
                        data-testid="registry-pdf"
                        displayValue={registryFilename}
                        requestStatus=""
                      />
                    ) : (
                      <ImagePreview
                        data-testid="registry-image"
                        imageBase64={registryBase64}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )}

            {/* New car invoice image */}
            {newCarInvoiceBase64 !== '' && (
              <Grid item xs={12} data-testid="new-car-invoice-container">
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      className={classes.fieldLabel}
                    >
                      {profileType === 'retencion'
                        ? 'Certificado de póliza actual'
                        : 'Proforma del auto'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    {isPdfNewCar ? (
                      <PdfPreview
                        data-testid="new-car-invoice-pdf"
                        displayValue={newCarInvoiceFilename}
                        requestStatus=""
                      />
                    ) : (
                      <ImagePreview
                        data-testid="new-car-invoice-image"
                        imageBase64={newCarInvoiceBase64}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )}
          </>
        )}
      </Grid>
    </DefaultCard>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  vinNumber: string;
  licensePlate: string;
  type: string;
  registryFilename: string;
  registryBase64: string;
  serialMotor: string;
  newCarInvoiceFilename: string;
  newCarInvoiceBase64: string;
  quotation: Quotation;
} => ({
  vinNumber: state.inspectionForm.textValues.vinNumber,
  licensePlate: state.inspectionForm.textValues.licensePlate,
  type: state.inspectionForm.textValues.type,
  registryFilename: state.inspectionForm.vehicleRegistryFileName,
  registryBase64: state.inspectionForm.vehicleRegistryImage,
  serialMotor: state.inspectionForm.textValues.serialMotor,
  newCarInvoiceFilename: state.emissionPolicy.newCarInvoiceFilename,
  newCarInvoiceBase64: state.emissionPolicy.newCarInvoiceBase64,
  quotation: state.quotation.quotation,
});

const connector = connect(mapStateToProps, {});
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  summary: QuotationSummaryResponse;
  preventCollapse?: boolean;
  isOptionsDetails?: boolean;
}

export default connector(FullVehicleDataCard);
